import { useTreeViewState } from '@aibees/elephant/context/tree-view';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { useTreeItem2 } from '@mui/x-tree-view/useTreeItem2';
import React, { ForwardedRef, useCallback, useMemo } from 'react';
import { TreeItemData } from '../types';
import { getItemChildValues, getItemParentValues } from '../utils';
import * as SX from './sx';
import type { CustomTreeItemProps as Props } from './types';
import { CustomTreeItemContent } from './custom-tree-item-content';

function getContentSx(leftPadding: boolean, marginBottom: boolean) {
  if (leftPadding && marginBottom) return SX.contentLpMb;
  if (leftPadding) return SX.contentLp;
  if (marginBottom) return SX.contentMb;
  return SX.contentDefault;
}

const CustomTreeItemInner = React.forwardRef(
  (
    {
      className,
      id,
      itemId,
      label,
      disabled,
      children,
      onClickHandler,
      checkboxPosition,
      multiSelect,
      metadataApiLoading,
      metadataEnabled,
      iconSx,
      avartarFallbackIcon,
      ...other
    }: Props,
    ref: ForwardedRef<HTMLLIElement>
  ) => {
    const selectedValues = useTreeViewState();

    const {
      getCheckboxProps,
      getContentProps,
      getGroupTransitionProps,
      getIconContainerProps,
      getLabelProps,
      getRootProps,
      publicAPI,
      status,
    } = useTreeItem2({
      id,
      itemId,
      children,
      label,
      disabled,
      rootRef: ref,
    });

    const item = publicAPI.getItem(itemId) as TreeViewBaseItem<TreeItemData>;
    const itemParent = item.parentId
      ? (publicAPI.getItem(item.parentId) as TreeViewBaseItem<TreeItemData>)
      : undefined;

    const parentValues = getItemParentValues(item, publicAPI);
    const hasChildrenOrParent = !!children || !!item.parentId;
    const hasAnyNephews = !!itemParent?.children?.some((i) => !!i.children);

    const selectedSet = useMemo(
      () => new Set(selectedValues),
      [selectedValues]
    );

    const { checked, indeterminate } = useMemo(() => {
      let c =
        selectedSet.has(item.value) ||
        parentValues.some((parentValue) => selectedSet.has(parentValue));
      let ind = false;

      if (item.children) {
        const childValues = item.children.map((i) => i.value);
        const allChildValues = getItemChildValues(item);

        if (
          childValues.length > 0 &&
          childValues.every((childValue) => selectedSet.has(childValue))
        ) {
          c = true;
        } else if (
          allChildValues.some((childValue) => selectedSet.has(childValue))
        ) {
          ind = true;
        }
      }
      return { checked: c, indeterminate: ind };
    }, [selectedSet, item, parentValues]);

    const handleCheckboxClick = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, chkd: boolean) => {
        event.stopPropagation();

        if (children) {
          onClickHandler(chkd, indeterminate, item.id, item.value);
        }
      },
      [children, indeterminate, item.id, item.value, onClickHandler]
    );

    const handleItemClick = useCallback(
      (e: React.MouseEvent<HTMLLIElement>) => {
        e.stopPropagation();
        if (item.disabled) return;
        getRootProps(other).onClick?.(e);
        if (!multiSelect || !children) {
          onClickHandler(
            !multiSelect ? true : !checked,
            !multiSelect ? true : indeterminate,
            item.id,
            item.value
          );
        }
      },
      [
        children,
        checked,
        indeterminate,
        item.disabled,
        item.id,
        item.value,
        multiSelect,
        onClickHandler,
        getRootProps,
        other,
      ]
    );

    const contentSx = getContentSx(
      !multiSelect ||
        !hasChildrenOrParent ||
        (!hasAnyNephews && !!item.parentId),
      !!(
        item.metadata?.iconSubtitle ||
        item.subtitle ||
        item.metadata?.subtitle
      )
    );

    return (
      <CustomTreeItemContent
        checked={checked}
        indeterminate={indeterminate}
        item={item}
        itemId={itemId}
        itemParent={itemParent}
        getRootProps={getRootProps}
        getContentProps={getContentProps}
        getCheckboxProps={getCheckboxProps}
        getLabelProps={getLabelProps}
        getGroupTransitionProps={getGroupTransitionProps}
        getIconContainerProps={getIconContainerProps}
        status={status}
        handleItemClick={handleItemClick}
        handleCheckboxClick={handleCheckboxClick}
        checkboxPosition={checkboxPosition}
        multiSelect={multiSelect}
        metadataApiLoading={metadataApiLoading}
        metadataEnabled={metadataEnabled}
        iconSx={iconSx}
        avartarFallbackIcon={avartarFallbackIcon}
        children={children}
        other={other}
        contentSx={contentSx}
        hasChildrenOrParent={hasChildrenOrParent}
        hasAnyNephews={hasAnyNephews}
        className={className}
      />
    );
  }
);

CustomTreeItemInner.displayName = 'CustomTreeItem';

export const CustomTreeItem = React.memo(CustomTreeItemInner);
